import { Box, Tab, Tabs } from '@mui/material';
import { useMemo } from 'react';
import { useSession } from '@/components/session/useSession';
import { useGlobalSearchContext } from '@/contexts/GlobalSearchContext';
import { SearchResultItem } from './type';
import { getGroupLabel } from './utils';

export default function GlobalSearchTabs() {
  const {
    org: { orgSector },
  } = useSession();

  const { state, send } = useGlobalSearchContext();
  const tabsHaveFocus = state.matches({ open: { navigating: 'tabs' } });
  const results = state.context.results;
  const tabs = state?.context.tabs;
  const selectedTab = state?.context.tabs.at(state?.context.selectedTabIndex ?? 0);

  const typeCounts = useMemo(() => {
    const counts: Record<SearchResultItem['type'], number> = {
      all: results.length,
      building: 0,
      'building-id': 0,
      'action-plan': 0,
      scenario: 0,
      portfolio: 0,
      'economic-unit': 0,
      navigation: 0,
    };

    results.forEach((item) => {
      counts[item.type] = (counts[item.type] || 0) + 1;
    });

    return counts;
  }, [results]);

  const parsedTabs = useMemo(() => {
    return tabs.map((type) => ({
      value: type,
      label: getGroupLabel(type, orgSector),
      count: typeCounts[type] || 0,
    }));
  }, [tabs, orgSector, typeCounts]);

  if (tabs.length === 0) return null;

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        px: 2,
        flexShrink: 0,
        backgroundColor: 'background.paper',
      }}
    >
      <Tabs
        value={selectedTab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 48,
          '& .MuiTabScrollButton-root': {
            '&:focus-visible': {
              borderRadius: 0.5,
              backgroundColor: (theme) => theme.palette.action.focus,
            },
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            minHeight: 48,
            fontWeight: 500,
            fontSize: '0.875rem',
            borderRadius: 0.5,
            ...(tabsHaveFocus && {
              '&:focus-visible': {
                backgroundColor: 'action.selected',
              },
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
              },
            }),
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
        }}
      >
        {parsedTabs.map((tab, index) => (
          <Tab
            key={tab.value}
            value={tab.value}
            id={selectedTab === tab.value ? 'global-search-selected-tab' : ``}
            onClick={() => {
              send({ type: 'SELECT_TAB', selectedTabIndex: index });
            }}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 0.5, p: 1 }}>
                {tab.label}
                {tab.count > 0 && (
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      backgroundColor: 'action.selected',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      minWidth: 20,
                      textAlign: 'center',
                      transition: (theme) => theme.transitions.create(['background-color']),
                    }}
                  >
                    {tab.count}
                  </Box>
                )}
              </Box>
            }
          />
        ))}
      </Tabs>
    </Box>
  );
}
